import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  forwardRef,
} from "react";
import PatientContext from "../../../context/PatientContext";
import "./index.css";
import ReactToPrint from "react-to-print";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import ProtocolTable from "./ProtocolTable";
import Disclaimer from "./Disclaimer";

const DEFAULT_UNITS = {
  Height: "cm",
  Weight: "kg",
  BMI: "kg/m2",
  "Heart Rate": "/min",
  "Total WBC": "cells/mcL",
  "Neutrophil Count": "%",
  "Lymphocyte Count": "%",
  Triglycerides: "mg/dL",
  "Total Cholesterol": "mg/dL",
  HDL: "mg/dL",
  LDL: "mg/dL",
};

const PrintPdfDetails = forwardRef((props, ref) => {
  const [patientInfo, setPatientInfo] = useState(props.PdfInfo);
  const { patientDetails } = useContext(PatientContext);
  const { contextPatientReport } = useContext(PatientContext);
  const [apidata, setAPI] = useState();
  const [units, setUnits] = useState(props.units);
  useEffect(() => {
    setPatientInfo(props.PdfInfo);
    setUnits(props.units);
    const report = contextPatientReport?.Data;
    setAPI(report);
  }, [patientDetails, props.PdfInfo, contextPatientReport, props.units]);

  const renderCurrentDate = () => {
    const date = new Date();
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  };

  const getUnit = (key) => {
    return (units && units[key]) || DEFAULT_UNITS[key];
  };

  let riskCardColor = "";

  riskCardColor = !apidata
    ? "risk-low-color"
    : apidata["Risk category"] === "High Risk"
    ? "risk-high-color"
    : apidata["Risk category"] === "Moderate Risk" ||
      apidata["Risk category"] === "Medium Risk"
    ? "risk-medium-color"
    : "risk-low-color";

  const loginDetails = Cookies.get("login_details")
    ? JSON.parse(Cookies.get("login_details"))
    : { executive: "" };

  return (
    <div className="pdf-container" ref={ref}>
      <div className="page">
        <div className="page-header">
          <h1 className="header-heading">Participant Report</h1>
          <img
            className="header-log"
            src="/apollo-white-logo.png"
            alt="apollo-logo"
          />
        </div>
        <div className="pdf-content-container">
          <ul className="pdf-user-info-container">
            {patientInfo?.basic_info &&
              Object.entries(patientInfo.basic_info).map(([key, value]) => (
                <li key={key}>
                  <p className="pdf-user-info-heading">{key}</p>:
                  <p className="pdf-user-info-value">{value}</p>
                </li>
              ))}
          </ul>
          <p className="line"></p>
          <h1 className="pdf-sub-heading">
            APOLLO HOSPITALS THANK YOU FOR YOUR CONSENT
          </h1>
          <p className="pdf-page-text">
            Based on current parameters, personal &#38; family history, AI
            enabled tools derive health risk scores to alert participant on
            his/her likely predisposition to a disease to aid early detection
            &#38; early medical intervention to mitigate the risk.
          </p>
          {patientInfo?.["Vital Signs"] && (
            <div className="details-container">
              <h2 className="details-header">PHYSICAL ATTRIBUTES</h2>
              <ul className="details-grid">
                {Object.entries(patientInfo["Vital Signs"]).map(
                  ([key, value]) => (
                    <li key={key} className="details-item">
                      <p className="details-key">
                        {key} {getUnit(key) && `(${getUnit(key)})`}
                      </p>
                      <p className="details-value">{value}</p>
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
          {patientInfo?.["Life Style"] && (
            <div className="details-container heart-health-container">
              <h2 className="details-header heart-health-header">
                LIFESTYLE ATTRIBUTES
              </h2>
              <ul className="details-grid">
                {Object.entries(patientInfo["Life Style"]).map(
                  ([key, value]) => (
                    <li key={key} className="details-item">
                      <p className="details-key">
                        {key} {getUnit(key) && `(${getUnit(key)})`}
                      </p>
                      <p className="details-value heart-health-value">{value}</p>
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
          {patientInfo?.["Family History"] && (
            <div className="details-container medical-container">
              <h2 className="details-header medical-header">FAMILY HISTORY</h2>
              <ul className="details-grid">
                {Object.entries(patientInfo["Family History"]).map(
                  ([key, value]) => (
                    <li key={key} className="details-item">
                      <p className="details-key">
                        {key} {getUnit(key) && `(${getUnit(key)})`}
                      </p>
                      <p className="details-value heart-health-value">{value}</p>
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
          {patientInfo?.["Medical History"] && (
            <div className="details-container medical-container">
              <h2 className="details-header medical-header">MEDICAL HISTORY</h2>
              <ul className="details-grid">
                {Object.entries(patientInfo["Medical History"]).map(
                  ([key, value]) => (
                    <li key={key} className="details-item">
                      <p className="details-key">
                        {key} {getUnit(key) && `(${getUnit(key)})`}
                      </p>
                      <p className="details-value heart-health-value">{value}</p>
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
          {patientInfo?.["Lab History"] && (
            <div className="details-container heart-health-container">
              <h2 className="details-header heart-health-header">LAB HISTORY</h2>
              <ul className="details-grid">
                {Object.entries(patientInfo["Lab History"]).map(
                  ([key, value]) => (
                    <li key={key} className="details-item">
                      <p className="details-key">
                        {key} {getUnit(key) && `(${getUnit(key)})`}
                      </p>
                      <p className="details-value medical-value">{value}</p>
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
        </div>
        <footer className="pdf-footer-container">
          <div className="d-flex flex-row justify-space-between plr-25">
            <p className="footer-text">Apollo Hospitals</p>
            <div className="d-flex flex-column alingn-item-end">
              <p className="footer-text mb-4">
                This report is accessed by{" "}
                <span className="text-capital">{loginDetails.executive}</span>
              </p>
              <p className="footer-text">{renderCurrentDate()}</p>
            </div>
          </div>
          <div className="style-line-container">
            <div className="line-1"></div>
            <div className="line-2"></div>
          </div>
        </footer>
      </div>
      <div className="page">
        <div className="page-header">
          <h1 className="header-heading">Participant Report</h1>
          <img
            className="header-log"
            src="/apollo-white-logo.png"
            alt="apollo-logo"
          />
        </div>
        <div className="pdf-content-container">
          <div className="pdf-new-report-card pdf-report-margin-top">
            <h2 className="pdf-report-card-title">
              AI Calculated Risk Score Based on Your Inputs
            </h2>
            {apidata && (
              <div className="pdf-risk-section">
                <div className="pdf-risk-cards">
                  <div className="pdf-risk-card">
                    <h3 className="pdf-risk-card-title">Risk Status</h3>
                    <div className={`${riskCardColor} pdf-risk-highlight`}>
                      {apidata["Risk category"]}
                    </div>
                  </div>
                  <div className="pdf-risk-card">
                    <h3 className="pdf-risk-card-title">Risk Score</h3>
                    <p className={`pdf-risk-value`}>{apidata["Risk Score"]}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pdf-new-report-card">
            <h2 className="report-card-title">AI Recommended Protocol</h2>
            {apidata && <ProtocolTable data={apidata.CDSS} />}
          </div>
          <div className="pdf-new-report-card">
            <h2 className="report-card-title">Disclaimer</h2>
            <Disclaimer />
          </div>
        </div>
        <footer className="pdf-footer-container">
          <div className="d-flex flex-row justify-space-between plr-25">
            <p className="footer-text">Apollo Hospitals</p>
            <div className="d-flex flex-column alingn-item-end">
              <p className="footer-text mb-4">
                This report is accessed by{" "}
                <span className="text-capital">{loginDetails.executive}</span>
              </p>
              <p className="footer-text">{renderCurrentDate()}</p>
            </div>
          </div>
          <div className="style-line-container">
            <div className="line-1"></div>
            <div className="line-2"></div>
          </div>
        </footer>
      </div>
    </div>
  );
});

const HypertensionPdfDetails = (props) => {
  const componentRef = useRef();
  const navigate = useNavigate();

  const gotohome = () => {
    navigate("/");
  };

  return (
    <div className="pdf-report-button">
      <PrintPdfDetails
        ref={componentRef}
        PdfInfo={props.PdfInfo}
        units={props.units}
      />
      <div className="report-buttons-container">
        <button className="download-btn home-button" onClick={gotohome}>
          <img className="home-icon" src="/home-icon.svg" alt="download.svg" />
        </button>
        <ReactToPrint
          trigger={() => (
            <button className="download-btn download-button">
              <img
                className="download-icon"
                src="/cloud-download-outline.svg"
                alt="download.svg"
              />
            </button>
          )}
          content={() => componentRef.current}
        />
      </div>
    </div>
  );
};

export default HypertensionPdfDetails;
